import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BellSlashIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  useMarkAllNotificationsRead,
  useMarkNotification,
  useNotificationStats,
  useRecentNotifications,
} from '../../hooks/useNotifications';
import { Notification } from '../../services/notifications.service';
import { formatWhen, NotificationIcon } from './notificationDisplay';
import { cn } from '../../lib/utils';

interface NotificationDropdownProps {
  onClose: () => void;
}

// The latest notifications under the bell. Everything else, deleting included, is on the page.
export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useRecentNotifications(true);
  const { data: stats } = useNotificationStats();
  const markNotification = useMarkNotification();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.data ?? [];
  const unread = stats?.unread ?? 0;

  const open = (notification: Notification) => {
    if (!notification.isRead) markNotification.mutate({ id: notification.id, read: true });
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Notifications
          {unread > 0 && <span className="ml-2 text-xs font-medium text-gray-500">{unread} unread</span>}
        </h3>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
          >
            <CheckIcon className="mr-1 h-3.5 w-3.5" />
            Mark all read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-10 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        ) : isError ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">The notifications could not be loaded.</p>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-8 text-gray-500">
            <BellSlashIcon className="mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-900">You're all caught up</p>
            <p className="mt-0.5 text-xs">Nothing needs your attention right now.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => open(notification)}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50',
                    !notification.isRead && 'bg-indigo-50/50',
                  )}
                >
                  <NotificationIcon type={notification.type} className="mt-0.5 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm', notification.isRead ? 'text-gray-700' : 'font-semibold text-gray-900')}>
                      {notification.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500 line-clamp-2">{notification.message}</span>
                    <span className="mt-1 block text-xs text-gray-400">{formatWhen(notification.createdAt)}</span>
                  </span>
                  {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-600" aria-label="Unread" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-gray-200 px-4 py-2.5">
        <button
          type="button"
          onClick={() => {
            navigate('/notifications');
            onClose();
          }}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
        >
          View all notifications
        </button>
      </div>
    </div>
  );
};
